import { useApolloClient } from "@apollo/client";
import { useQueryClient } from "@tanstack/react-query";
import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEventListener } from "usehooks-ts";

import { REST_UNAUTHORIZED_EVENT } from "@/api/restClient";
import {
  broadcastLogout,
  closeLogoutChannel,
  setupLogoutListener,
} from "@/contexts/AuthContext/logoutBroadcast";
import { env } from "@/env";
import {
  clearAllLeagueGlobalFilters,
  clearAllStoredBulkUpdateSelections,
  useLocalStorage,
  usePopup,
} from "@/hooks";
import useAuth from "@/hooks/useAuth";
import { resetTerminologyOverrides } from "@/i18n";
import {
  clearGlobalSearchMode,
  clearRecentSearchItems,
} from "@/layouts/DashboardLayout/GlobalSearch";
import { exchangeHandoffToken, revokeRefreshToken, type TokenPair } from "@/utils/auth/authApi";
import { DASHBOARD_APP_ID } from "@/utils/auth/buildPortalUrl";
import { clearImpersonationSession } from "@/utils/auth/impersonation";
import { upgradeLegacySession } from "@/utils/auth/sessionUpgrade";
import { refreshAccessToken, storeTokenPair } from "@/utils/auth/tokenRefresh";

import { useBrandColorContext } from "../BrandColorContext";
import { clearCurrentLeagueId } from "../CurrentLeagueContext/CurrentLeagueContext.utils";
import { clearCurrentOrganizationId } from "../CurrentOrganizationContext/CurrentOrganizationContext.utils";
import { clearCurrentPlatformId } from "../CurrentPlatformContext/CurrentPlatformContext.utils";

import { AuthContext, AuthContextValueType, LoginSocialError } from "./AuthContext";
import { SocialLoginProvider } from "./AuthContext.types";
import {
  clearAccessTokenExpiresAt,
  clearRefreshToken,
  getAccessToken as getStoredAccessToken,
  getRefreshToken,
  hasForeignAudience,
  isTokenExpired,
} from "./AuthContext.utils";

/**
 * A handed-over pair minted for another app (the portal falls back to `client`
 * when `?app=` is lost on the way) would 403 on every dashboard call. Drop it
 * and revoke its family instead of adopting it; with no session the guards send
 * the user back to the portal with `app=dashboard`.
 */
const rejectForeignTokenPair = (tokenPair: TokenPair): boolean => {
  if (!hasForeignAudience(tokenPair.accessToken, DASHBOARD_APP_ID)) return false;

  console.error("Discarded a handed-over session minted for another app.");
  if (tokenPair.refreshToken) {
    void revokeRefreshToken(tokenPair.refreshToken);
  }
  return true;
};

export const AuthContextProvider = ({ children }: PropsWithChildren) => {
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const [loginSocialLoading, setLoginSocialLoading] = useState(false);
  const [loginSocialError, setLoginSocialError] = useState<LoginSocialError | null>(null);
  const auth = useAuth();
  // Raw useNavigate (not useScopedNavigate): logout must land on the bare root
  // "/", but the platform/org contexts still hold ids at this moment, so
  // useScopedNavigate would prefix "/" → "/platform/:p/entity/:o/".
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isWhiteLabeled, setBrandColor } = useBrandColorContext();

  const paramToken = searchParams.get("token");

  // Tournated-ID redirect-mode handoff: the portal redirects back to Admin with
  // the freshly-issued token in the URL fragment (`#accessToken=…&tournatedId=…`),
  // falling back to the query string. Read both. The fragment isn't part of
  // react-router's `searchParams`, so parse `window.location.hash` directly.
  const hashParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.hash.replace(/^#/, ""))
      : new URLSearchParams();
  const portalToken = searchParams.get("accessToken") ?? hashParams.get("accessToken");

  // DEV-4415 handoff mode: instead of a ready-made access token the portal (or
  // another Tournated app) hands over a single-use handoff token, which we
  // exchange for an access + refresh pair scoped to this app. Both handoffs are
  // accepted during the migration.
  const handoffToken = hashParams.get("handoffToken") ?? searchParams.get("handoffToken");

  const { open: openPopup, close: closePopup, isOpen } = usePopup();

  const [accessToken, setAccessToken] = useLocalStorage<string | null>("accessToken", null, {
    subscribe: "currentDocument",
  });

  const [isHandoffPending, setIsHandoffPending] = useState(false);
  // A stored refresh token can revive an expired access token, so bootstrap it
  // before the guards get to look at the (expired) token and bounce to the portal.
  const [isBootstrappingSession, setIsBootstrappingSession] = useState(
    () => Boolean(getRefreshToken()) && isTokenExpired(getStoredAccessToken())
  );
  // Handoff tokens are single-use: a second exchange of the same token 401s and
  // is logged server-side as a reuse attempt. `logout` (an effect dependency)
  // changes identity whenever its own dependencies do, so guard by token value.
  const exchangedHandoffTokens = useRef<Set<string>>(new Set());

  useEventListener("message", async (event) => {
    if (event.origin !== env.authUrl) {
      return;
    }

    try {
      const data = JSON.parse(event.data);

      // REST-mode portals post a single-use `handoffToken` instead of a
      // ready-made `accessToken`; exchange it for a token pair.
      if (typeof data.handoffToken === "string" && data.handoffToken) {
        const tokenPair = await exchangeHandoffToken(data.handoffToken);
        if (rejectForeignTokenPair(tokenPair)) {
          closePopup();
          setLoginSocialLoading(false);
          setLoginSocialError(new LoginSocialError("invalidData"));
          return;
        }
        storeTokenPair(tokenPair);
        setAccessToken(tokenPair.accessToken);
        closePopup();
        setLoginSocialLoading(false);
        setLoginSocialError(null);
        return;
      }

      if (!data.accessToken) {
        console.error("No access token found in event data");
        setLoginSocialLoading(false);
        setLoginSocialError(new LoginSocialError("invalidData"));
        return;
      }

      setAccessToken(data.accessToken);
      closePopup();
      setLoginSocialLoading(false);
      setLoginSocialError(null);
    } catch (error) {
      closePopup();
      setLoginSocialLoading(false);
      setLoginSocialError(new LoginSocialError("invalidData"));
      console.error("❌ Failed to parse message data", error);
    }
  });

  const loginSocial = useCallback(
    async ({ provider }: { provider: SocialLoginProvider }) => {
      setLoginSocialLoading(true);
      setLoginSocialError(null);

      // `app` is REQUIRED: the portal defaults `?app=` to `client`, and the
      // session it hands back carries that audience. A `client`-audience token
      // is rejected by every dashboard controller (403 APP_AUDIENCE_MISMATCH),
      // so omitting it signs the user in and then forbids every request.
      const searchParams = new URLSearchParams({
        provider,
        app: DASHBOARD_APP_ID,
        origin: window.location.origin,
      });

      openPopup({
        url: `${env.authUrl}/?${searchParams.toString()}`,
      });
    },
    [openPopup]
  );

  useEffect(() => {
    if (!isOpen) {
      setLoginSocialLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const cleanup = setupLogoutListener(() => {
      console.info("Received logout event. Performing logout.");
      auth.logout();
      clearImpersonationSession();
      clearCurrentPlatformId();
      clearCurrentOrganizationId();
      clearCurrentLeagueId();
      clearAllLeagueGlobalFilters();
      clearAllStoredBulkUpdateSelections();
      clearRecentSearchItems();
      clearGlobalSearchMode();
      resetTerminologyOverrides();
      setAccessToken(null);
      clearRefreshToken();
      clearAccessTokenExpiresAt();
      queryClient.clear();
      apolloClient.resetStore();
      if (!isWhiteLabeled) {
        setBrandColor(null);
      }
      navigate("/", { replace: true });
    });

    const handleUnauthorized = () => {
      console.info("Received 401 from REST API. Performing logout.");
      auth.logout();
      clearImpersonationSession();
      clearCurrentPlatformId();
      clearCurrentOrganizationId();
      clearCurrentLeagueId();
      clearAllLeagueGlobalFilters();
      clearAllStoredBulkUpdateSelections();
      clearRecentSearchItems();
      clearGlobalSearchMode();
      resetTerminologyOverrides();
      setAccessToken(null);
      clearRefreshToken();
      clearAccessTokenExpiresAt();
      queryClient.clear();
      apolloClient.resetStore();
      if (!isWhiteLabeled) {
        setBrandColor(null);
      }
      navigate("/", { replace: true });
    };
    window.addEventListener(REST_UNAUTHORIZED_EVENT, handleUnauthorized);

    return () => {
      cleanup();
      closeLogoutChannel();
      window.removeEventListener(REST_UNAUTHORIZED_EVENT, handleUnauthorized);
    };
  }, [apolloClient, auth, isWhiteLabeled, navigate, queryClient, setAccessToken, setBrandColor]);

  // `redirect` (default true) sends the user back to the bare root after a real
  // logout. The token-handoff effects below flush the session but pass
  // `redirect: false` so they stay on the inbound deep link instead of bouncing
  // to the default platform/entity home.
  const logout = useCallback(
    async ({ redirect = true }: { redirect?: boolean } = {}) => {
      // Revoke the refresh-token family + session server-side before dropping
      // the token locally. Best-effort and deliberately not awaited (it is sent
      // with `keepalive`, so it survives the teardown) — a slow or failing auth
      // API must never block the user from logging out.
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        void revokeRefreshToken(refreshToken);
      }

      broadcastLogout();

      clearImpersonationSession();
      clearCurrentPlatformId();
      clearCurrentOrganizationId();
      clearCurrentLeagueId();
      clearAllLeagueGlobalFilters();
      clearAllStoredBulkUpdateSelections();
      clearRecentSearchItems();
      clearGlobalSearchMode();
      resetTerminologyOverrides();
      setAccessToken(null);
      clearRefreshToken();
      clearAccessTokenExpiresAt();
      if (!isWhiteLabeled) {
        setBrandColor(null);
      }
      auth.logout();
      apolloClient.resetStore();
      queryClient.clear();
      if (redirect) {
        navigate("/", { replace: true });
      }
    },
    [apolloClient, auth, isWhiteLabeled, navigate, queryClient, setAccessToken, setBrandColor]
  );

  useEffect(() => {
    if (paramToken) {
      logout({ redirect: false });
      setAccessToken(paramToken);

      setSearchParams(
        (prev) => {
          prev.delete("token");
          return prev;
        },
        { replace: true }
      );
    }
  }, [logout, paramToken, setAccessToken, setSearchParams]);

  // Store the token returned by the Tournated-ID portal, then strip it from the
  // URL (query + fragment) so it can't be bookmarked, shared, or logged.
  useEffect(() => {
    if (!portalToken) return;

    // Flush any prior session before adopting the portal-issued token, so a
    // warm-tab account switch can't leak the previous user's cached data
    // (mirrors the legacy `?token=` handoff above). Skip the logout redirect so
    // the inbound deep link (e.g. `/platform/:p/entity/:o/users?search=…`) is
    // preserved instead of being replaced by the default platform/entity home.
    logout({ redirect: false });
    setAccessToken(portalToken);

    setSearchParams(
      (prev) => {
        prev.delete("accessToken");
        prev.delete("tournatedId");
        return prev;
      },
      { replace: true }
    );

    if (typeof window !== "undefined" && window.location.hash) {
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
    }
  }, [logout, portalToken, setAccessToken, setSearchParams]);

  // Exchange an inbound handoff token for this app's own token pair, then strip
  // it from the URL exactly like the legacy access-token handoff above. A failed
  // exchange (expired, already redeemed, backend down) simply leaves the user
  // unauthenticated, so the guards send them to the portal.
  useEffect(() => {
    if (!handoffToken) return;
    // Single-use token: a second exchange 401s and is logged server-side as a
    // reuse attempt. This ref makes the network exchange fire exactly once for a
    // given token even though the effect re-runs whenever `logout` changes
    // identity — which is often, because the JWT context value is a fresh object
    // every render. Crucially the exchange below must NOT be gated on an
    // `isActive`/cleanup flag: a re-run mid-flight would otherwise abandon the
    // in-flight exchange before it strips the URL and clears `isHandoffPending`,
    // leaving `isInitialized` stuck false and the guards hanging on a blank
    // screen (with the consumed token still in the URL, so a reload 401s).
    if (exchangedHandoffTokens.current.has(handoffToken)) return;
    exchangedHandoffTokens.current.add(handoffToken);

    setIsHandoffPending(true);

    const stripHandoffFromUrl = () => {
      setSearchParams(
        (prev) => {
          prev.delete("handoffToken");
          prev.delete("tournatedId");
          return prev;
        },
        { replace: true }
      );

      if (typeof window !== "undefined" && window.location.hash) {
        window.history.replaceState({}, "", window.location.pathname + window.location.search);
      }
    };

    const exchange = async () => {
      let tokenPair: TokenPair | null = null;

      try {
        tokenPair = await exchangeHandoffToken(handoffToken);
      } catch (error) {
        // Expired, already redeemed (e.g. a reload of a consumed link), or the
        // backend is down: fall through with no pair so the token is stripped and
        // the guards bounce the now-unauthenticated user to the portal, rather
        // than leaving a dead token in the URL to be retried on every reload.
        console.error("Failed to exchange the handoff token:", error);
      }

      if (tokenPair && !rejectForeignTokenPair(tokenPair)) {
        // Flush any prior session before adopting the new pair, for the same
        // reason as the portal-token path above: a warm-tab account switch must
        // not leak the previous user's cached data. `redirect: false` keeps the
        // inbound deep link.
        logout({ redirect: false });
        storeTokenPair(tokenPair);
        setAccessToken(tokenPair.accessToken);
      }

      stripHandoffFromUrl();
      setIsHandoffPending(false);
    };

    void exchange();
  }, [handoffToken, logout, setAccessToken, setSearchParams]);

  // An expired access token backed by a stored refresh token is recoverable:
  // refresh before the guards run (they hold on `isInitialized`). A rejected
  // refresh clears every auth cell, so they then bounce to the portal as before.
  useEffect(() => {
    if (!isBootstrappingSession) return;

    let isActive = true;

    const bootstrap = async () => {
      const token = await refreshAccessToken();

      if (!isActive) return;

      if (token) {
        setAccessToken(token);
      }
      setIsBootstrappingSession(false);
    };

    void bootstrap();

    return () => {
      isActive = false;
    };
  }, [isBootstrappingSession, setAccessToken]);

  // A stored session minted for another app — adopted before the handoff check
  // above existed, or arriving through a legacy token path — is unusable: every
  // dashboard call 403s and refreshing keeps its audience, so the browser stays
  // stuck. End it (revoking its family) so the guards re-authenticate through
  // the portal with `app=dashboard`.
  useEffect(() => {
    if (!hasForeignAudience(accessToken, DASHBOARD_APP_ID)) return;

    console.error("Ending a stored session minted for another app.");
    void logout({ redirect: false });
  }, [accessToken, logout]);

  // DEV-4415: one-shot, fire-and-forget upgrade of a legacy bearer-only session
  // (unexpired token, no refresh token, no `typ` claim) to an access + refresh
  // pair. Deliberately independent of `isInitialized` — it must never delay
  // rendering, and any failure silently keeps the legacy session. An inbound
  // token handoff establishes a fresh session anyway, so the attempt waits
  // until the handoff effects above have consumed and stripped it; the
  // module-scoped once-guard inside `upgradeLegacySession` then caps the whole
  // thing at one attempt per page lifetime.
  useEffect(() => {
    if (paramToken || portalToken || handoffToken) return;
    void upgradeLegacySession();
  }, [handoffToken, paramToken, portalToken]);

  const contextValue = useMemo(
    (): AuthContextValueType => ({
      isAuthenticated: accessToken !== null,
      // Hold guards until any inbound token (legacy `?token=`, the ID portal
      // handoff, or a handoff token still being exchanged) has been processed and
      // until a refresh-token bootstrap has settled, so they don't bounce to the
      // portal prematurely.
      isInitialized:
        !paramToken &&
        !portalToken &&
        !handoffToken &&
        !isHandoffPending &&
        !isBootstrappingSession,
      accessToken,
      loginSocialLoading,
      loginSocialError,
      loginSocial,
      logout,
      setAccessToken,
    }),
    [
      accessToken,
      handoffToken,
      isBootstrappingSession,
      isHandoffPending,
      loginSocial,
      loginSocialError,
      loginSocialLoading,
      logout,
      paramToken,
      portalToken,
      setAccessToken,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
